import React, { useState } from "react";
import AdManager from './Ads/AdManager';
import { FaShoppingCart, FaShieldAlt, FaCheckCircle, FaSpinner, FaCreditCard, FaTruck, FaPercentage } from 'react-icons/fa';

const OrderSummary = ({ 
  checkOutData, 
  totalOrderedAmount, 
  totalDeliveryFees, 
  totalPaymentAmount,
  PlaceOrder 
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedSummary, setExpandedSummary] = useState(false);

  const handlePlaceOrder = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    try {
      await PlaceOrder();
    } catch (error) {
      console.error("Order placement error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const itemCount = checkOutData?.length || 0;
  const isBuyNowOrder = checkOutData?.some(item => item.buyNow === true);
  const savings = 0; // Calculate if there are discounts

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 sticky top-8 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center">
            <FaShoppingCart className="mr-2" />
            Order Summary
          </h2>
          {isBuyNowOrder && (
            <span className="bg-white/20 backdrop-blur-sm text-xs font-medium px-2 py-1 rounded-full">
              Express Order
            </span>
          )}
        </div>
      </div>

      <div className="p-6">
        {/* Quick Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 font-medium">Items ({itemCount}):</span>
            <span className="font-semibold text-gray-800">
              KSH {totalOrderedAmount.toLocaleString('en-KE')}
            </span>
          </div>
          
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 font-medium flex items-center">
              <FaTruck className="mr-1 text-sm" />
              Shipping:
            </span>
            <span className="font-semibold text-gray-800">
              {totalDeliveryFees > 0 ? `KSH ${totalDeliveryFees.toLocaleString('en-KE')}` : 'FREE'}
            </span>
          </div>
          
          {savings > 0 && (
            <div className="flex items-center justify-between mb-2">
              <span className="text-green-600 font-medium flex items-center">
                <FaPercentage className="mr-1 text-sm" />
                You save:
              </span>
              <span className="font-semibold text-green-600">
                -KSH {savings.toLocaleString('en-KE')}
              </span>
            </div>
          )}
        </div>
        
        {/* Total */}
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <span className="font-bold text-gray-800 text-lg">Order Total:</span>
            <span className="font-bold text-2xl text-orange-600">
              KSH {totalPaymentAmount.toLocaleString('en-KE')}
            </span>
          </div>
          {totalDeliveryFees === 0 && (
            <p className="text-sm text-green-600 mt-1 flex items-center">
              <FaCheckCircle className="mr-1" />
              Free shipping included
            </p>
          )}
        </div>
        

        
        {/* Security & Trust Indicators */}
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <FaShieldAlt className="text-green-600" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center space-x-1">
              <FaCheckCircle className="text-green-600" />
              <span>Money Back Guarantee</span>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-xs text-gray-500">
              By placing this order, you agree to our 
              <button className="text-orange-600 hover:underline mx-1">Terms of Service</button>
              and 
              <button className="text-orange-600 hover:underline mx-1">Privacy Policy</button>
            </p>
          </div>
        </div>
        
        {/* Detailed Summary Toggle */}
        <button
          onClick={() => setExpandedSummary(!expandedSummary)}
          className="w-full mt-4 text-sm text-gray-600 hover:text-orange-600 transition-colors"
        >
          {expandedSummary ? 'Hide' : 'Show'} detailed breakdown
        </button>
        
        {expandedSummary && (
          <div className="mt-4 space-y-2 text-sm border-t pt-4">
            {checkOutData?.map((item, index) => (
              <div key={index} className="flex justify-between">
                <span className="text-gray-600 truncate">
                  {item.productName} (×{item.quantity})
                </span>
                <span className="text-gray-800 font-medium ml-2">
                  KSH {(item.price * item.quantity).toLocaleString('en-KE')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Place Order Button */}
      <div className="p-6 border-t border-gray-100">
        <button
          type="button"
          onClick={handlePlaceOrder}
          disabled={isProcessing}
          className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 shadow-md
            ${isProcessing 
              ? 'bg-yellow-400 cursor-not-allowed' 
              : 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 transform hover:scale-105 hover:shadow-lg'}
          `}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing Payment...
            </span>
          ) : (
            'Place your order'
          )}
        </button>
        
        {isProcessing && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800 text-center">
              ⏳ Confirming your payment with the backend. Please do not close this page...
            </p>
          </div>
        )}
        
        <p className="text-xs text-gray-500 text-center mt-3">
          By placing your order, you agree to our terms and conditions
        </p>
        
        {/* Checkout targeted advertisements */}
        <div className="mt-4">
          <AdManager 
            location="checkout"
            context={{ 
              orderTotal: totalPaymentAmount,
              itemCount: checkOutData.length,
              isProcessing: isProcessing
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;